import { HttpClient } from '@angular/common/http';
import { CurrentUserService } from 'src/app/core/services/current-user.service';
import { environment } from 'src/environments/environment';
import { FlashMessageService } from './flash-message.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { Storage } from 'src/app/core/services/storage.service';
import { StorageCollections } from 'src/app/core/enum/storage-collections.enum';
import * as _ from 'lodash';
import { GoogleAnalyticsService } from './google-analytics/google-analytics.service';

@Injectable({
  providedIn: 'root'
})
export class AppNotificationService {
  public persona: string;
  public notifications: BehaviorSubject<any> = new BehaviorSubject([]);
  public unreadCount: BehaviorSubject<number> = new BehaviorSubject(0);
  public lockScroll: boolean = false;
  public notificationsEnabled: boolean = true;
  public refreshNotifications
  private organizationType;
  private _notificationBuffer: any[] = [];
  public url = environment.runtime.hubconnectPortalUrl;

  constructor(private authHttp: HttpClient, private currentUserService: CurrentUserService,
    private flashMessageService: FlashMessageService,
    private _gaSvc: GoogleAnalyticsService,
    private router: Router,
    private storage: Storage) {
    this.setup()
  }

  async setup() {
    await this.currentUserService.setIsImpersonator();
    this.persona = this.currentUserService.toTitleCase(_.get(this.currentUserService, 'user.organizationType', ''));

    const user = (await this.currentUserService.getUser());

    if (user) {
      this.organizationType = user.organizationType;
      if (this.organizationType === "INTERNAL") this.notificationsEnabled = false;

      if (this.notificationsEnabled) {
        this.refreshNotifications = setInterval(() => {
          this.lockScroll = false;
          this.loadNotifications('general', 0)
        },
          1000 * 60 * 2); //will reload notifications every 2 minutes
      }
    }
  }

  public async getNotificationEnabledStatus() {
    this.organizationType = (await this.currentUserService.getUser()).organizationType
    return this.organizationType === "INTERNAL" ? false : true
  }

  public async notificationHandler(notificationEvent) {
    // If user impersonator retain notifications
    if (this.currentUserService.isImpersonator) {
      notificationEvent.updatedNotifications = _.cloneDeep(this._notificationBuffer);
    }

    this.notifications.next(notificationEvent.updatedNotifications)
    this.countUnread()
    if (notificationEvent.eventName === "read_one") {
      if (!this.currentUserService.isImpersonator) {
        this.markRead(notificationEvent.data)
      }
    }
    else if (notificationEvent.eventName === "read_one_and_route") {
      if (!this.currentUserService.isImpersonator) {
        this.markRead(notificationEvent.data)
      }

      const path = notificationEvent.data.notificationContent.path
      if (path === '/announcements') { // nnouncement Notification clicked
        // Need to redirect to hubconnect-uesr-management-app
        this.router.navigateByUrl(path);
      }
      else if (path === '/dashboard/track-and-trace') { // Shipment notification clicked
        const orderId = _.get(notificationEvent, 'data.notificationContent.additionalData.orderId', null)
        if (!orderId) this.flashMessageService.updateMessage('There was a problem with the notification');
        else {
          this.storage.setItem('hub_shipment_id', orderId, StorageCollections.HUBCONNECT);
          const config = await this.storage.getItem('config', StorageCollections.CONFIG);
          const baseUrl = 'https://hubconnect{{TARGET}}.hubgroup.com'.replace('{{TARGET}}', JSON.parse(config).runtime.envName);
          window.location.href = `${baseUrl}/customers${path}`;
        }
      }
      else if (path === '/dashboard/rate-inquiry/spot-rate') { // Spot Rate Notification clicked
        const spotRateId = _.get(notificationEvent, 'data.notificationContent.additionalData.rateId')
        if (!spotRateId) this.flashMessageService.updateMessage('There was a problem with the notification');
        else {
          this.storage.setItem('hub_spot_rate_id', spotRateId, StorageCollections.HUBCONNECT);
          this.storage.setItem('rate_inquiry_tab', 2, StorageCollections.HUBCONNECT);
          const config = await this.storage.getItem('config', StorageCollections.CONFIG);
          const baseUrl = 'https://hubconnect{{TARGET}}.hubgroup.com'.replace('{{TARGET}}', JSON.parse(config).runtime.envName);
          window.location.href = `${baseUrl}/customers${path}`;
        }
      }
      else { //
        this.flashMessageService.updateMessage('There was a problem with the notification')
      }
    }
    else if (notificationEvent.eventName === "read_all") {
      if (!this.currentUserService.isImpersonator) {
        this.markAllRead(notificationEvent.data.type)
      }
    }
    else if (notificationEvent.eventName === "clear_one") {
      if (!this.currentUserService.isImpersonator) {
        this.clear(notificationEvent.data)
      }
    }
    else if (notificationEvent.eventName === "clear_all") {
      if (!this.currentUserService.isImpersonator) {
        this.clearAll(notificationEvent.data.type)
      }
    }
    else if (notificationEvent.eventName === "clear_all_subtype") {
      if (!this.currentUserService.isImpersonator) {
        this.clearAllSubType(notificationEvent.data.type, notificationEvent.data.subType)
      }
    }
    else if (notificationEvent.eventName === "read_all_subtype") {
      if (!this.currentUserService.isImpersonator) {
        this.readAllSubType(notificationEvent.data.type, notificationEvent.data.subType)
      }
    }
  }

  public loadNotifications(type: string, page: number) {
    return this.authHttp.get(`${this.url}/api/user/appNotifications/${type}/${page}`).toPromise().then((notifications: any) => {
      this.notifications.next(notifications)
      this._notificationBuffer = _.cloneDeep(notifications);
      this.countUnread()
    }).catch(e => {
      this.notifications.next([])
      this.countUnread()
      this.flashMessageService.updateMessage('There was a problem getting notifications');
    });
  }

  public markRead(notification: any) {
    const payload = {
      type: notification.type,
      notificationId: notification._id
    }
    return this.authHttp.post(`${this.url}/api/user/appNotifications/read`, payload).toPromise().catch(e => {
      this.flashMessageService.updateMessage('There was a problem updating notification status');
    });
  }

  public markAllRead(type: string) {
    const payload = {
      type: type
    }
    return this.authHttp.post(`${this.url}/api/user/appNotifications/read-all`, payload).toPromise().catch(e => {
      this.flashMessageService.updateMessage('There was a problem updating notification status');
    });
  }

  public readAllSubType(type: string, subType: string) {
    const payload = {
      type: type,
      subtype: subType
    }
    return this.authHttp.post(`${this.url}/api/user/appNotifications/read-all-subtype`, payload).toPromise().catch(e => {
      this.flashMessageService.updateMessage('There was a problem updating notification status');
    });
  }

  public clear(notification: any) {
    const payload = {
      type: notification.type,
      notificationId: notification._id
    }
    return this.authHttp.post(`${this.url}/api/user/appNotifications/clear`, payload).toPromise().catch(e => {
      this.flashMessageService.updateMessage('There was a problem clearing notification');
    });
  }

  public clearAll(type: string) {
    const payload = {
      type: type
    }
    return this.authHttp.post(`${this.url}/api/user/appNotifications/clear-all`, payload).toPromise().catch(e => {
      this.flashMessageService.updateMessage('There was a problem clearing notifications');
    });
  }

  public clearAllSubType(type: string, subType: string) {
    const payload = {
      type: type,
      subtype: subType
    }
    return this.authHttp.post(`${this.url}/api/user/appNotifications/clear-all-subtype`, payload).toPromise().catch(e => {
      this.flashMessageService.updateMessage('There was a problem updating notification status');
    });
  }

  public getHighPriorityNotification(type: string) {
    return this.authHttp.get(`${this.url}/api/user/appPriorityNotifications/get-high-priority-notification/${type}`).toPromise().then((notification: any) => {
      return notification
    }).catch(e => {
      this.flashMessageService.updateMessage('There was a problem fetching notifications');
    });
  }

  public clearHighPriorityNotifications(type: string) {
    if (!this.currentUserService.isImpersonator) {
      const payload = {
        type: type
      }
      return this.authHttp.post(`${this.url}/api/user/appPriorityNotifications/clear-high-priority`, payload).toPromise().catch(e => {
        this.flashMessageService.updateMessage('There was a problem clearing notifications');
      });
    }
  }

  public sendGA(data) {
    this._gaSvc.register('AppNotification', `${this.persona} ` + data, this.currentUserService.user.organizationName);
  }

  public loadNextNotificationPage(page) {
    const type = 'general'
    if (page === 0) {
      this.loadNotifications(type, page)
    }
    else {
      return this.authHttp.get(`${this.url}/api/user/appNotifications/${type}/${page}`).toPromise().then((notifications: any) => {
        if (!notifications.length) this.lockScroll = true;
        // TODO: REMOVE DUPLICATES CAUSE THERE MIGHT BE SOME
        this.notifications.next(this.notifications.getValue().concat(notifications))
        this._notificationBuffer = _.cloneDeep(notifications);
        this.countUnread()
      }).catch(e => {
        this.flashMessageService.updateMessage('There was a problem getting notifications');
        this.notifications.next(this.notifications.getValue())
        this.countUnread()
      });
    }
  }

  countUnread() {
    let temp = 0;
    let notifications = this.notifications.value
    for (let i = 0; i < notifications.length; i += 1) {
      if (notifications[i].notificationContent.unread === true) {
        temp += 1;
      }
    }
    this.unreadCount.next(temp);
  }
}
